"use client";

import process from "node:process";
import { CLOUDFLARE_SITE_ID, CLOUDFLARE_USE_TURNSTILE_IN_BOOKER } from "@calcom/lib/constants";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { EmailField, TextAreaField, TextField } from "@calcom/ui/components/form";
import type { FormEvent } from "react";
import { useEffect, useRef, useState } from "react";
import TurnstileWidget from "../../auth/components/Turnstile";

type FormStatus = "idle" | "sending" | "success" | "error";

function readField(form: FormData, key: string): string {
  const value = form.get(key);
  return typeof value === "string" ? value : "";
}

function failureMessage(status: number, t: (key: string) => string): string {
  if (status === 400) return t("site_contact_invalid");
  if (status === 401) return t("site_contact_verification_failed");
  if (status === 429) return t("site_contact_rate_limited");
  if (status >= 500) return t("site_contact_send_failed");
  return t("site_contact_error");
}

export function ContactForm() {
  const { t } = useLocale();
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [cfToken, setCfToken] = useState<string | undefined>();
  const [widgetKey, setWidgetKey] = useState(0);
  const successRef = useRef<HTMLParagraphElement>(null);
  const captchaRequired =
    CLOUDFLARE_USE_TURNSTILE_IN_BOOKER === "1" &&
    Boolean(CLOUDFLARE_SITE_ID) &&
    process.env.NEXT_PUBLIC_IS_E2E !== "1";

  useEffect(() => {
    if (status === "success") successRef.current?.focus();
  }, [status]);

  if (status === "success") {
    return (
      <p ref={successRef} tabIndex={-1} role="status" className="text-base text-default leading-7">
        {t("site_contact_success")}
      </p>
    );
  }

  function resetCaptcha() {
    setCfToken(undefined);
    setWidgetKey((key) => key + 1);
  }

  function fail(message: string) {
    resetCaptcha();
    setErrorMessage(message);
    setStatus("error");
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setStatus("sending");
    setErrorMessage(undefined);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: readField(form, "name"),
          email: readField(form, "email"),
          message: readField(form, "message"),
          cfToken,
        }),
      });
      if (!response.ok) {
        fail(failureMessage(response.status, t));
        return;
      }
      setStatus("success");
    } catch {
      fail(t("site_contact_error"));
    }
  }

  return (
    <form className="flex max-w-xl flex-col gap-4" onSubmit={onSubmit} noValidate>
      <TextField
        name="name"
        label={t("site_contact_name_label")}
        required
        maxLength={120}
        autoComplete="name"
      />
      <EmailField
        name="email"
        label={t("site_contact_email_label")}
        required
        maxLength={320}
        autoComplete="email"
      />
      <TextAreaField
        name="message"
        label={t("site_contact_message_label")}
        required
        placeholder=""
        rows={5}
        maxLength={2000}
      />
      {captchaRequired ? (
        <TurnstileWidget
          key={widgetKey}
          appearance="interaction-only"
          onVerify={(token) => {
            setCfToken(token);
          }}
        />
      ) : null}
      {errorMessage ? (
        <p role="alert" className="text-error text-sm">
          {errorMessage}
        </p>
      ) : null}
      <div>
        <Button
          type="submit"
          color="primary"
          disabled={status === "sending" || (captchaRequired && !cfToken)}>
          {status === "sending" ? t("site_contact_sending") : t("site_contact_submit")}
        </Button>
      </div>
    </form>
  );
}
